import { useCallback, useEffect, useState } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Button,
  Badge,
  ThemeIcon,
  Alert,
  Loader,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconBellRinging, IconBellOff, IconShieldCheck } from "@tabler/icons-react";
import { Endpoints } from "../../lib/endpoints";
import { PerformRequest } from "../../hooks/usePerformRequest";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const SW_PATH = "/sw.js";

export function AdminProofPushAlerts() {
  const [envSupported, setEnvSupported] = useState(true);
  const [serverReady, setServerReady] = useState<boolean | null>(null);
  const [vapidPublicKey, setVapidPublicKey] = useState<string | null>(null);
  const [browserSubscribed, setBrowserSubscribed] = useState(false);
  const [loadingState, setLoadingState] = useState(true);
  const [busy, setBusy] = useState(false);

  const refreshBrowserSubscription = useCallback(async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setEnvSupported(false);
      setBrowserSubscribed(false);
      return;
    }
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) {
        setBrowserSubscribed(false);
        return;
      }
      const sub = await reg.pushManager.getSubscription();
      setBrowserSubscribed(!!sub);
    } catch {
      setBrowserSubscribed(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingState(true);
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        setEnvSupported(false);
        setServerReady(false);
        setLoadingState(false);
        return;
      }

      const ep = Endpoints.AdminPushVapidPublicKey();
      const res = await PerformRequest<{
        data?: { publicKey?: string; configured?: boolean };
        message?: string;
      }>({
        method: ep.method,
        route: ep.route,
      });
      if (cancelled) return;
      if (res.status === 503) {
        setServerReady(false);
        setVapidPublicKey(null);
      } else if (res.status && res.status >= 400) {
        setServerReady(null);
        setVapidPublicKey(null);
      } else {
        const pk = res.data?.data?.publicKey;
        if (pk) {
          setVapidPublicKey(pk);
          setServerReady(true);
        } else {
          setServerReady(false);
          setVapidPublicKey(null);
        }
      }

      await refreshBrowserSubscription();
      if (!cancelled) setLoadingState(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshBrowserSubscription]);

  const handleEnable = async () => {
    if (!vapidPublicKey) return;
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        notifications.show({
          title: "Notifications blocked",
          message:
            perm === "denied"
              ? "Enable notifications for this site in your browser settings, then try again."
              : "Permission was not granted.",
          color: "red",
        });
        setBusy(false);
        return;
      }

      const registration = await navigator.serviceWorker.register(SW_PATH, {
        scope: "/",
      });
      await navigator.serviceWorker.ready;

      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(
          vapidPublicKey,
        ) as BufferSource,
      });

      const json = sub.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        throw new Error("Invalid subscription from browser");
      }

      const saveEp = Endpoints.AdminPushSubscribe();
      const saveRes = await PerformRequest<{
        data?: unknown;
        message?: string;
      }>({
        method: saveEp.method,
        route: saveEp.route,
        data: { subscription: json },
      });
      if (!saveRes.status || saveRes.status >= 400) {
        const apiMsg =
          saveRes.message ||
          (saveRes.data as { message?: string } | undefined)?.message;
        throw new Error(apiMsg || "Failed to save subscription");
      }

      setBrowserSubscribed(true);
      notifications.show({
        title: "Alerts enabled",
        message: "You will get a browser notification when a new proof needs review.",
        color: "teal",
        icon: <IconShieldCheck size={18} />,
      });
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Could not enable push alerts.";
      notifications.show({
        title: "Something went wrong",
        message: msg,
        color: "red",
      });
    } finally {
      setBusy(false);
      await refreshBrowserSubscription();
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      const endpoint = sub?.endpoint;
      if (sub) {
        await sub.unsubscribe();
      }
      if (endpoint) {
        const unsEp = Endpoints.AdminPushUnsubscribe();
        const unsRes = await PerformRequest({
          method: unsEp.method,
          route: unsEp.route,
          data: { endpoint },
        });
        if (!unsRes.status || unsRes.status >= 400) {
          throw new Error(unsRes.message || "Failed to remove subscription on server");
        }
      }
      setBrowserSubscribed(false);
      notifications.show({
        title: "Alerts turned off",
        message: "This browser will no longer receive proof notifications.",
        color: "gray",
      });
    } catch (e: any) {
      notifications.show({
        title: "Could not disable",
        message: e?.response?.data?.message || e?.message || "Try again.",
        color: "red",
      });
    } finally {
      setBusy(false);
      await refreshBrowserSubscription();
    }
  };

  if (loadingState) {
    return (
      <Paper withBorder p="md" radius="md" bg="dark.8">
        <Group gap="sm">
          <Loader size="sm" color="gray" />
          <Text size="sm" c="dimmed">
            Checking notification settings…
          </Text>
        </Group>
      </Paper>
    );
  }

  if (!envSupported) {
    return (
      <Alert variant="light" color="gray" title="Browser alerts not available">
        <Text size="sm">
          Push notifications require a modern desktop browser (Chrome, Edge, or Firefox)
          with notifications support. Safari has limited support.
        </Text>
      </Alert>
    );
  }

  if (serverReady === false) {
    return (
      <Alert variant="light" color="orange" title="Server push not configured">
        <Text size="sm">
          Set <strong>VAPID_PUBLIC_KEY</strong>, <strong>VAPID_PRIVATE_KEY</strong>, and{" "}
          <strong>VAPID_SUBJECT</strong> (e.g. <code>mailto:you@domain.com</code>) on the API.
          Generate keys with:{" "}
          <code style={{ wordBreak: "break-all" }}>npx web-push generate-vapid-keys</code>
        </Text>
        <Text size="sm" mt="xs" c="dimmed">
          Set <strong>ADMIN_URL</strong> to the exact URL of this admin site so notification clicks open the proof queue.
        </Text>
      </Alert>
    );
  }

  if (serverReady === null) {
    return (
      <Alert variant="light" color="red" title="Could not load push settings">
        <Text size="sm">Refresh the page. If the problem continues, sign in again or contact support.</Text>
      </Alert>
    );
  }

  return (
    <Paper
      withBorder
      p="lg"
      radius="md"
      style={{
        background:
          "linear-gradient(135deg, rgba(18, 184, 134, 0.08) 0%, rgba(34, 139, 230, 0.06) 100%)",
      }}
    >
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="md" align="flex-start" wrap="nowrap">
            <ThemeIcon size={48} radius="md" variant="gradient" gradient={{ from: "teal", to: "blue", deg: 135 }}>
              <IconBellRinging size={26} />
            </ThemeIcon>
            <div>
              <Text fw={600} size="lg">
                Browser proof alerts
              </Text>
              <Text size="sm" c="dimmed" maw={520}>
                Get a native notification in Chrome (even when this tab is in the background)
                whenever a user submits a proof that needs manual review.
              </Text>
            </div>
          </Group>
          {browserSubscribed ? (
            <Badge size="lg" variant="light" color="teal" leftSection={<IconShieldCheck size={14} />}>
              On
            </Badge>
          ) : (
            <Badge size="lg" variant="light" color="gray">
              Off
            </Badge>
          )}
        </Group>

        <Group gap="sm">
          {!browserSubscribed ? (
            <Button
              leftSection={<IconBellRinging size={18} />}
              onClick={handleEnable}
              loading={busy}
              disabled={!vapidPublicKey}
              gradient={{ from: "teal", to: "blue", deg: 105 }}
              variant="gradient"
            >
              Turn on notifications
            </Button>
          ) : (
            <Button
              leftSection={<IconBellOff size={18} />}
              variant="light"
              color="gray"
              onClick={handleDisable}
              loading={busy}
            >
              Turn off for this browser
            </Button>
          )}
        </Group>
      </Stack>
    </Paper>
  );
}
